import React from 'react';
import { colors } from '../../theme/tokens/colors';
import { PlayIcon, PauseIcon } from '../icons';
import './PlayButtonXL.css';

export interface PlayButtonXLProps {
    /** Size of the button in pixels (default: 98) */
    size?: number;
    /** Whether audio is currently playing */
    isPlaying: boolean;
    /** Whether audio is loading */
    isLoading?: boolean;
    /** Progress value from 0 to 100 */
    progress: number;
    /** Click handler to toggle play/pause */
    onClick: () => void;
    /** Optional className for additional styling */
    className?: string;
}

/**
 * PlayButtonXL - An extra-large circular play/pause button with animated progress ring
 * Designed for SoundCloud-style audio player UI - larger than PlayButtonLarge for hero/featured sections
 */
export const PlayButtonXL: React.FC<PlayButtonXLProps> = ({
    size = 98,
    isPlaying,
    isLoading = false,
    progress,
    onClick,
    className = '',
}) => {
    // Calculate SVG dimensions
    const strokeWidth = 6;
    const radius = (size - strokeWidth) / 2;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    // Ring color based on playing/loading state
    const ringColor = isLoading ? colors.accentYellow : (isPlaying ? colors.accentYellow : colors.lightGrey);

    return (
        <button
            className={`play-button-xl ${isLoading ? 'play-button-xl--loading' : ''} ${className}`}
            onClick={onClick}
            style={{ width: size, height: size }}
            aria-label={isLoading ? 'Loading' : (isPlaying ? 'Pause' : 'Play')}
            type="button"
        >
            {/* Progress Ring SVG */}
            <svg
                className={`play-button-xl__ring-container ${isLoading ? 'play-button-xl__ring-container--spinning' : ''}`}
                width={size}
                height={size}
                viewBox={`0 0 ${size} ${size}`}
            >
                {/* Background ring (track) */}
                <circle
                    className="play-button-xl__ring-bg"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                />
                {/* Progress ring */}
                <circle
                    className="play-button-xl__ring-progress"
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    strokeWidth={strokeWidth}
                    stroke={ringColor}
                    strokeDasharray={circumference}
                    strokeDashoffset={isLoading ? circumference * 0.75 : strokeDashoffset}
                    strokeLinecap="round"
                />
            </svg>

            {/* Icon - rendered directly without wrapper */}
            <div className="play-button-xl__icon">
                {isPlaying ? (
                    <PauseIcon size="l" tone="inherit" style={{ width: 80, height: 80 }} />
                ) : (
                    <PlayIcon size="l" tone="inherit" style={{ width: 80, height: 80 }} />
                )}
            </div>
        </button>
    );
};

export default PlayButtonXL;
